import { BubbleList } from '@app/components/BubbleList/BubbleList';
import { AlsoAvailableOnSection } from '@app/pages/i/components/AlsoAvailableOnSection';
import { ArtistAttribution } from '@app/pages/i/components/ArtistAttribution';
import { DiscoveryUnit } from '@app/pages/i/components/DiscoveryUnit';
import { LicensedProductsSection } from '@app/pages/i/components/LicensedProductsSection';
import { TagsSection } from '@app/pages/i/components/Tags/TagsSection';
import { TrustSignalsGroup } from '@app/pages/i/components/TrustSignalsGroup';
import { ProductPageProps } from '@app/pages/i/types';
import Stack from '@redbubble/design-system/src/Stack';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import type { UserInfo } from '@redbubble/next/types';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import { memo, useDeferredValue } from 'react';
import { useCurrentInventoryStore } from '../../hooks/useCurrentInventoryItem';
import SimilarDesignsContainer from '../SimilarDesignsContainer/SimilarDesignsContainer';

export const SecondaryContentSection: React.FC = memo(() => {
  const [{ locale }] = useUserInfo<UserInfo>();
  const {
    bubbles,
    inventoryCategorySet,
    iaEntityCopy,
    defaultInventoryItems,
    trendingSearchTopics,
    searchProductContext,
  } = usePageProps<ProductPageProps>();

  const { id, productTypeId } = useCurrentInventoryStore(
    (state) => state.currentInventoryItem
  );
  const deferredId = useDeferredValue(id);
  const { id: defaultItemId, work } = inventoryCategorySet.defaultItem;

  return (
    <Stack spacing="xxl" paddingX="l">
      <ErrorBoundary>
        {defaultInventoryItems && (
          <AlsoAvailableOnSection
            work={work}
            availableProducts={defaultInventoryItems}
            defaultItemId={defaultItemId}
            productTypeId={productTypeId}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        {work.licensed && work.artist && (
          <LicensedProductsSection
            workId={Number(work.id)}
            artistUsername={work.artist.username}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        {work.artist && (
          <ArtistAttribution
            inventoryItemId={deferredId}
            artist={work.artist}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        <SimilarDesignsContainer />
      </ErrorBoundary>
      <ErrorBoundary>
        <DiscoveryUnit searchProductContext={searchProductContext}>
          {bubbles && <BubbleList bubbles={bubbles} />}
        </DiscoveryUnit>
      </ErrorBoundary>
      <ErrorBoundary>
        <TagsSection
          work={work}
          locale={locale}
          iaEntityCopy={iaEntityCopy}
          trendingSearchTags={trendingSearchTopics}
        />
        <TrustSignalsGroup />
      </ErrorBoundary>
    </Stack>
  );
});
